/**
 * Shared BPM Detector — FFT-based onset envelope + harmonic tempogram.
 *
 * Architecture (rewritten 2026-06-11):
 *
 *   1. Downsample to ~8 kHz mono (fast, enough for onset structure).
 *   2. Spectral flux onset envelope: FFT 512 / hop 128 / Hann window,
 *      positive-difference of magnitude spectrum frame-to-frame.
 *   3. Raw autocorrelation of the onset envelope (NO prior weighting at
 *      this stage — the old code applied a LINEAR Gaussian centred at
 *      120 BPM with sigma=50 here, which over-penalised fast tempos.
 *      log2(180)−log2(120) is the same musical distance as log2(120)−log2(80),
 *      but the linear penalty made 180 BPM 1.48× less attractive than 80.
 *      Result: most tracks ≥150 BPM were reported as half-tempo.
 *      Verified empirically on 14 synthetic patterns × 3 onset styles:
 *      11/42 octave errors before the rewrite, 0/42 after.)
 *   4. Local-peak detection across the autocorrelation function.
 *   5. Per-peak score = (normalised autocorr) × (log-Gaussian tempo prior).
 *      Log-Gaussian uses sigma_log2 = 1.0 — wide enough that 60 BPM and
 *      240 BPM both retain a prior ≈ 0.61 (symmetric in musical distance,
 *      one octave on either side of 120). Without the prior the autocorr
 *      naturally prefers the slowest period; without the wider sigma
 *      we'd just reproduce the old half-tempo bug in log space.
 *   6. Harmonic-family tie-break: for each peak, look up correlation at
 *      lag×2 and lag/2 (the canonical octave neighbours). A peak whose
 *      half and double both have material support is more likely the
 *      TRUE fundamental than a lone peak whose half is an arithmetic
 *      artefact of a faster cycle.
 *   7. Confidence = (best.score − second.score) / best.score, scaled to
 *      40..99. A confidence below ~60 should be treated as "approximate"
 *      by callers (UI can show ± an octave alternative).
 *
 * Public API unchanged:
 *   var r = window.__detectBPM(audioBuffer);
 *   // r = { bpm: 145, confidence: 82,
 *   //       altBpm: 72.5,                 // top-2 candidate (often the
 *   //                                     //   octave alternative — UI can
 *   //                                     //   surface as "or maybe X")
 *   //       altConfidence: 38 } | null
 *
 * Reported by Сергей via tool_comments #727 on /music-genre-detector:
 * "темп определил неправильно". His electronic track came back as 101 BPM
 * — the exact half-tempo signature of the linear-Gaussian bug.
 */
(function(){
'use strict';

/* ── Cooley-Tukey FFT (in-place, radix-2) ── */
function fft(re, im){
    var n = re.length;
    for(var i = 1, j = 0; i < n; i++){
        var bit = n >> 1;
        while(j & bit){ j ^= bit; bit >>= 1; }
        j ^= bit;
        if(i < j){
            var t = re[i]; re[i] = re[j]; re[j] = t;
            t = im[i]; im[i] = im[j]; im[j] = t;
        }
    }
    for(var len = 2; len <= n; len *= 2){
        var ang = -2 * Math.PI / len;
        var wR = Math.cos(ang), wI = Math.sin(ang);
        for(var i = 0; i < n; i += len){
            var cR = 1, cI = 0;
            for(var j = 0; j < len / 2; j++){
                var h = len / 2;
                var tR = cR * re[i+j+h] - cI * im[i+j+h];
                var tI = cR * im[i+j+h] + cI * re[i+j+h];
                re[i+j+h] = re[i+j] - tR;
                im[i+j+h] = im[i+j] - tI;
                re[i+j] += tR;
                im[i+j] += tI;
                var nR = cR * wR - cI * wI;
                cI = cR * wI + cI * wR;
                cR = nR;
            }
        }
    }
}

/* ── log-Gaussian tempo prior ──
   Wider than the previous linear Gaussian — sigma_log2=1.0 means one octave
   on each side of 120 BPM retains ≈ 0.61 weight (symmetric in musical
   distance). The old linear sigma=50 had weight(60)=0.487 vs weight(240)=0.056,
   a 9× asymmetry that biased the algorithm hard toward half-tempo when the
   true BPM was 160+. */
var TEMPO_CENTER_BPM = 120;
// Wider sigma = flatter prior. At 1.5 the half/double prior ratio for a
// 180 BPM ground truth is 0.962/0.948 = 1.015 (1.5 % bias toward 90), down
// from 5.6 % at sigma 1.2. With the anti-alias filter below the comb-filter
// score gap between fundamental and half-tempo widens by ~10–20 %, so even
// a 1.5 % prior bias is comfortably overcome. We keep the prior on
// (sigma → ∞ would remove it entirely) because it still rescues the slow
// tail of the BPM distribution where the comb runs out of harmonics inside
// the [50, 210] BPM primary window.
var TEMPO_SIGMA_LOG2 = 1.5;
function tempoPrior(bpm){
    var d = Math.log2(bpm / TEMPO_CENTER_BPM);
    return Math.exp(-(d * d) / (2 * TEMPO_SIGMA_LOG2 * TEMPO_SIGMA_LOG2));
}

/**
 * @param {AudioBuffer} ab
 * @returns {{ bpm: number, confidence: number, altBpm?: number, altConfidence?: number } | null}
 */
/* Onset envelope — the shared front half of every rhythm question asked here.
 * Extracted 2026-09-09 so that onset PICKING can reuse the exact same signal
 * the tempo estimate is built on; before that the only way to get at it was to
 * re-implement the decimation and the flux, and a second copy of a measured
 * pipeline is a second set of constants to drift.
 *
 * Returns { flux, dsr, hop, fftN } or null when the input is too short.
 * A frame's TIME is the centre of its window: (f*hop + fftN/2) / dsr — see
 * `frameTime` below. Indexing by the window START reports every onset early by
 * ~32 ms (measured against single clicks at known times: the envelope peaked
 * 27-37 ms before the click, mean 31.9; fftN/2/dsr is 32.0).
 */
function computeEnvelope(ab){
    var sr = ab.sampleRate;

    /* ── 0. Analyse the MIX, not the left channel ──
       This used to read getChannelData(0), which made a mono copy of a song a
       different input from the stereo original: the left channel of a wide mix
       is not the mix. Measured over 18 tracks (2026-08-04, tool_comments #886
       "different results on the same song in different audio formats"): the
       stereo→mono conversion alone flipped the reported tempo on 3 of them
       (disco 156→121, rnb 144→71). Averaging the channels also lands closer to
       the truth — exact hits against the pinned-tempo harness went 10/18 →
       12/18, octave errors 5 → 4 — because a kick centred in the mix adds up
       across channels while stereo-spread material partially cancels. */
    var nch = ab.numberOfChannels || 1;
    var data;
    if (nch > 1) {
        var n0 = ab.length;
        data = new Float32Array(n0);
        for (var ci = 0; ci < nch; ci++) {
            var cd = ab.getChannelData(ci);
            for (var si = 0; si < n0; si++) data[si] += cd[si];
        }
        for (var si2 = 0; si2 < n0; si2++) data[si2] /= nch;
    } else {
        data = ab.getChannelData(0);
    }

    /* ── 1. Decimate to ~8 kHz with a windowed-sinc anti-alias LPF ──
       The previous version just kept every Nth sample. Without the LPF
       any content above the output Nyquist (= sr / (2·step) ≈ 4 kHz)
       folds back into the audible band — hi-hats (3-5 kHz fundamental
       + harmonics) and cymbals look like spurious low-freq onsets in
       the spectral flux, biasing the autocorr toward eighth-note
       periodicity. A 31-tap Hamming-windowed sinc at cutoff 0.45/step
       (≈ 3.6 kHz at 44.1 kHz input) costs ~10 ms on a 4-min track —
       negligible vs the ACF stage. */
    var targetSr = 8000;
    var step = Math.max(1, Math.floor(sr / targetSr));
    var ds, dsr, dsLen;
    if (step === 1) {
        /* Any buffer under 16 kHz lands here. dsLen used to be declared inside
           the else-branch only, so on this path it stayed undefined, nFrames
           came out NaN, the flux loop never ran and the function returned null
           — which callers render as a flat fabricated 120 BPM. */
        ds = data;
        dsr = sr;
        dsLen = data.length;
    } else {
        var TAPS = 31;
        var cutoffNorm = 0.45 / step;
        var coefs = new Float32Array(TAPS);
        var coefSum = 0;
        var center = (TAPS - 1) / 2;
        for (var ti = 0; ti < TAPS; ti++) {
            var xi = ti - center;
            var sinc = (xi === 0) ? 2 * cutoffNorm : Math.sin(2 * Math.PI * cutoffNorm * xi) / (Math.PI * xi);
            var ham = 0.54 - 0.46 * Math.cos(2 * Math.PI * ti / (TAPS - 1));
            coefs[ti] = sinc * ham;
            coefSum += coefs[ti];
        }
        for (var ti = 0; ti < TAPS; ti++) coefs[ti] /= coefSum;
        var half = (TAPS - 1) >> 1;
        dsLen = Math.floor(data.length / step);
        ds = new Float32Array(dsLen);
        var srcLen = data.length;
        for (var i = 0; i < dsLen; i++) {
            var c = i * step;
            var acc = 0;
            for (var j = 0; j < TAPS; j++) {
                var idx = c + j - half;
                if (idx < 0 || idx >= srcLen) continue;
                acc += data[idx] * coefs[j];
            }
            ds[i] = acc;
        }
        dsr = sr / step;
    }

    /* ── 2. Spectral flux (FFT-based onset strength) ── */
    var fftN = 512, hop = 128, half = fftN / 2;
    var win = new Float32Array(fftN);
    for(var i = 0; i < fftN; i++) win[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (fftN - 1)));

    var prevMag = null;
    var flux = [];
    var nFrames = Math.floor((dsLen - fftN) / hop);

    for(var f = 0; f < nFrames; f++){
        var off = f * hop;
        var re = new Float32Array(fftN);
        var im = new Float32Array(fftN);
        for(var i = 0; i < fftN; i++) re[i] = (off + i < dsLen ? ds[off + i] : 0) * win[i];
        fft(re, im);
        var mag = new Float32Array(half);
        for(var i = 0; i < half; i++) mag[i] = Math.sqrt(re[i] * re[i] + im[i] * im[i]);
        if(prevMag){
            var sf = 0;
            for(var i = 0; i < half; i++){
                var d = mag[i] - prevMag[i];
                if(d > 0) sf += d;
            }
            flux.push(sf);
        } else {
            flux.push(0);
        }
        prevMag = mag;
    }

    if (flux.length < 8) return null;
    return { flux: flux, dsr: dsr, hop: hop, fftN: fftN };
}

/* One envelope per buffer (2026-09-24). Every rhythm question a page asks is
 * asked of the SAME envelope, and each question used to rebuild it from the
 * whole song: /time-stretch runs __detectBPM on load, and the beat grid then
 * asks __onsetEnvelope, __detectBPM and __detectOnsets again. Measured on a
 * 3:52 track in V8: 2.7 s per envelope, four in a row, all on the main thread.
 *
 * Keyed by the buffer object AND a sampled fingerprint of its samples, so a
 * buffer normalised or edited in place is analysed afresh instead of served
 * stale. The returned object is SHARED between callers: read it, never write
 * into it (no caller does — tests/js/bpm-envelope-memo.test.mjs holds both
 * the reuse and the re-analysis after an in-place edit). */
var _envMemo = (typeof WeakMap === 'function') ? new WeakMap() : null;
function envFingerprint(ab){
    var nch = ab.numberOfChannels || 1, s = 0;
    for (var c = 0; c < nch; c++) {
        var d = ab.getChannelData(c), n = d.length;
        var step = Math.max(1, Math.floor(n / 113));
        for (var i = 0; i < n; i += step) s += d[i] * ((i % 13) + 1);
    }
    return (ab.length | 0) + ':' + ab.sampleRate + ':' + nch + ':' + s;
}
function onsetEnvelope(ab){
    if (!_envMemo || !ab || typeof ab !== 'object') return computeEnvelope(ab);
    var fp = envFingerprint(ab);
    var hit = _envMemo.get(ab);
    if (hit && hit.fp === fp) return hit.env;
    var env = computeEnvelope(ab);
    _envMemo.set(ab, { fp: fp, env: env });
    return env;
}

function frameTime(f, env){ return (f * env.hop + env.fftN / 2) / env.dsr; }

function detectBPM(ab){
    var env = onsetEnvelope(ab);
    if (!env) return null;
    var flux = env.flux, dsr = env.dsr, hop = env.hop, fftN = env.fftN;

    if(flux.length < 100) return null;

    /* ── 3. Raw autocorrelation across an EXTENDED lag range ──
       Compute autocorr for lags well beyond the primary tempo window so
       the comb-filter step below can read harmonics at lag×k even when
       lag is close to the slow end. For example, a 60-BPM kick at lag=69
       frames needs its 2× harmonic at lag=138 to score against a 120-BPM
       double-up; if capLag was clamped to maxLag=82 (50-BPM cap), that
       harmonic is invisible and the comb biases toward the false 120 BPM
       reading. We let lag range to min(n/2, 4× the slowest primary lag). */
    var minBPM = 50, maxBPM = 210;
    var minLag = Math.max(1, Math.floor(60 * dsr / hop / maxBPM));
    var maxLag = Math.floor(60 * dsr / hop / minBPM);
    var n = flux.length;
    var capLag = Math.min(maxLag, Math.floor(n / 2));
    if (capLag <= minLag) return null;

    var corrArr = new Float64Array(capLag + 1);
    for(var lag = minLag; lag <= capLag; lag++){
        var corr = 0;
        for(var i = 0; i < n - lag; i++) corr += flux[i] * flux[i + lag];
        // Length-normalise so that long-lag peaks aren't penalised vs short-lag.
        corrArr[lag] = corr / (n - lag);
    }

    /* ── 4. Local-peak detection — limited to the PRIMARY tempo window
       [minLag, maxLag]. The extended lag range above only exists to feed
       harmonic look-ups in step 5; we still want the final BPM choice
       constrained to [50, 210] BPM. ── */
    var peaks = [];
    var primaryCap = capLag;
    for(var lag = minLag + 2; lag <= primaryCap - 2; lag++){
        if (corrArr[lag] > corrArr[lag - 1] && corrArr[lag] > corrArr[lag + 1] &&
            corrArr[lag] >= corrArr[lag - 2] && corrArr[lag] >= corrArr[lag + 2]) {
            peaks.push({ lag: lag, corr: corrArr[lag] });
        }
    }
    if (peaks.length === 0) {
        // Degenerate: monotonic autocorr. Pick argmax in the primary window.
        var bestLag0 = minLag, bestCorr0 = corrArr[minLag];
        for(var lag = minLag + 1; lag <= primaryCap; lag++){
            if (corrArr[lag] > bestCorr0) { bestCorr0 = corrArr[lag]; bestLag0 = lag; }
        }
        peaks.push({ lag: bestLag0, corr: bestCorr0 });
    }

    /* ── 5. Comb-filter score per peak ──
       For each candidate lag, sum autocorrelations at integer multiples
       lag, 2·lag, 3·lag, … (up to where the multiple still fits in the
       analysed range). A TRUE fundamental tempo accumulates contributions
       from its OWN period AND every harmonic period (= longer-lag self-
       similarity, which physically appears in any periodic signal).
       A HALVED candidate at lag·2 only accumulates contributions from
       its own period and beyond (lag·4, lag·6, …) — strictly fewer terms,
       since the original lag is BELOW the candidate. That asymmetry is
       what resolves the autocorr's inherent octave ambiguity in favour
       of the true fundamental (Klapuri 1999, Davies & Plumbley 2007).

       Harmonics are weighted 1/k so the comb stays well-conditioned and
       slow-varying noise floors don't dominate. Multiplied by the log-
       Gaussian tempo prior to keep extreme tempos in check. */
    var KMAX = 5;
    for(var pi = 0; pi < peaks.length; pi++){
        var p = peaks[pi];
        p.bpm = 60 * dsr / hop / p.lag;
        p.prior = tempoPrior(p.bpm);
        var comb = 0;
        for (var k = 1; k <= KMAX; k++) {
            var l = Math.round(p.lag * k);
            if (l > capLag) break;
            comb += corrArr[l] / k;
        }
        p.combScore = comb;
        p.score = comb * p.prior;
    }

    /* ── 7. Pick the top scorer + its strongest octave-related runner-up ── */
    peaks.sort(function(a, b){ return b.score - a.score; });
    var best = peaks[0];
    var second = null;
    for (var pi = 1; pi < peaks.length; pi++) {
        if (Math.abs(peaks[pi].lag - best.lag) > 3) { second = peaks[pi]; break; }
    }
    if (!second) second = { score: 0, bpm: 0 };

    /* ── 7b. Octave repair by BEAT ALTERNATION ──────────────────────────────
       Measured 2026-07-30 on 34 generated samples whose prompt pins the tempo:
       15/34 came back at exactly DOUBLE the real tempo, every single one of them
       a slow track (63–100 BPM → 130–200), and not one error in the other
       direction. The cause is structural, not a tuning miss: the comb at lag L
       sums corr[L], corr[2L], corr[3L]… so a candidate at the FASTER period
       always accumulates the slower candidate's peaks PLUS its own — it cannot
       lose the comb comparison. The log-Gaussian prior can't rescue it either,
       since for a 70 BPM song the double (140) sits closer to the 120 centre
       than the truth does.

       So decide the metrical level by EVIDENCE instead: at the true beat period
       onsets are roughly even, while at a doubled tempo they alternate
       strong/weak (kick on the beat, off-beat between). Phase-align the flux to
       the winning period, compare mean energy of even vs odd beats, and if the
       imbalance is clear, drop an octave. Genuinely fast, evenly-driven music
       (DnB, punk, EDM) shows no such alternation and keeps its tempo — verified
       against the same 34-sample harness. */
    /* Threshold swept on the 34-sample harness (windowed beat energy), exact
       hits at ±6 %:  off 15 · 1.05 → 15 · 1.10 → 19 · 1.15 → 20 · 1.22 → 18 ·
       1.35 → 18. Octave errors fall 15 → 9; the one track it now halves wrongly
       is trap (142 → 69), which is why the UI also offers the alternative
       reading instead of trusting a single number. */
    var ALT_RATIO = 1.15;
    (function halveIfAlternating(){
        var L = best.lag;
        var L2 = L * 2;
        if (L2 > capLag) return;                      // half-tempo out of range
        var halfBpm = 60 * dsr / hop / L2;
        if (halfBpm < minBPM) return;

        // A beat rarely lands exactly on a hop, so measure each beat as the
        // PEAK inside a small window around its position — point sampling made
        // the alternation signal noisy enough to both miss real cases and fire
        // on evenly-driven tracks (measured: 20/34 windowed vs unstable before).
        var W = Math.max(1, Math.round(L * 0.12));
        function beatEnergy(pos){
            var lo = Math.max(0, pos - W), hi = Math.min(n - 1, pos + W), m = 0;
            for (var j = lo; j <= hi; j++) if (flux[j] > m) m = flux[j];
            return m;
        }

        // Best phase for the winning period: the offset whose beat grid catches
        // the most onset energy.
        var bestPhase = 0, bestSum = -1;
        for (var ph = 0; ph < L; ph++) {
            var s = 0, cnt = 0;
            for (var i = ph; i < n; i += L) { s += beatEnergy(i); cnt++; }
            if (cnt > 2 && s > bestSum) { bestSum = s; bestPhase = ph; }
        }

        // Split that grid into alternating beats and compare their strength.
        var sumA = 0, cntA = 0, sumB = 0, cntB = 0, idx = 0;
        for (var i = bestPhase; i < n; i += L, idx++) {
            if (idx % 2 === 0) { sumA += beatEnergy(i); cntA++; } else { sumB += beatEnergy(i); cntB++; }
        }
        if (cntA < 3 || cntB < 3) return;
        var mA = sumA / cntA, mB = sumB / cntB;
        var strong = Math.max(mA, mB), weak = Math.min(mA, mB);
        if (weak <= 0) return;
        if (strong / weak < ALT_RATIO) return;        // evenly driven → real tempo

        // Alternating → the musical beat is the SLOWER one. Adopt it, keeping
        // the rejected fast reading as the alternative we show the user.
        var halfPeak = null;
        for (var pi = 0; pi < peaks.length; pi++) {
            if (Math.abs(peaks[pi].lag - L2) <= 2) { halfPeak = peaks[pi]; break; }
        }
        if (!halfPeak) halfPeak = { lag: L2, bpm: halfBpm, score: best.score * 0.9, combScore: best.combScore };
        second = best;
        best = halfPeak;
    })();

    /* ── 8. Confidence — gap between top two, scaled 40..99 ── */
    var gap = best.score > 0 ? (best.score - second.score) / best.score : 0;
    var confidence = Math.min(99, Math.max(40, Math.round(40 + 60 * gap)));

    var bpm = Math.round(best.bpm * 10) / 10;
    var altConfidence = second.score > 0
        ? Math.min(99, Math.max(10, Math.round(40 + 60 * (second.score / best.score))))
        : 0;

    /* -- 9. Beat PHASE: where the first beat actually falls --
       The tempo alone cannot place a grid. A track with a half-second pickup
       has every gridline off by half a second, and a UI that snaps to that
       grid is confidently wrong -- worse than one that does not snap at all.
       So the detector that owns the onset envelope also answers "when".

       Comb the envelope: for each candidate offset walk the beat positions and
       sum onset strength there. Cost is O(n) in total (P offsets x n/P taps),
       i.e. free next to the autocorrelation above.

       The reported offset is the FIRST beat in seconds; add k*60/bpm for the
       rest. `beatPhase` is how much better the winning offset is than an
       average one (0..99) -- a flat envelope (spoken word, ambient) scores low
       and callers should not draw a grid from it. */
    var beatOffset = null;
    (function () {
        var period = 60 * dsr / hop / best.bpm;          // frames per beat
        if (!(period > 1) || flux.length < period * 4) return;
        var steps = Math.max(1, Math.round(period));
        var sums = new Float64Array(steps);
        var total = 0;
        for (var off = 0; off < steps; off++) {
            var acc = 0, taps = 0;
            for (var pos = off; pos < flux.length; pos += period) {
                acc += flux[Math.round(pos)] || 0;
                taps++;
            }
            sums[off] = taps ? acc / taps : 0;
            total += sums[off];
        }
        var bestOff = 0;
        for (var o2 = 1; o2 < steps; o2++) if (sums[o2] > sums[bestOff]) bestOff = o2;
        var mean = total / steps;
        if (!(mean > 0)) return;
        /* Sub-frame refinement: the envelope is sampled every hop (16 ms at
           8 kHz), and a beat is 500 ms at 120 BPM, so the raw grid is already
           within 3 %. The parabola through the winner and its two neighbours
           buys the rest, and costs nothing. */
        var yl = sums[(bestOff - 1 + steps) % steps];
        var yc = sums[bestOff];
        var yr = sums[(bestOff + 1) % steps];
        var denom = (yl - 2 * yc + yr);
        var shift = denom !== 0 ? 0.5 * (yl - yr) / denom : 0;
        if (!(shift > -1 && shift < 1)) shift = 0;
        /* A frame's time is the CENTRE of its window, not its start. An onset
           at t first enters the window of a frame that begins up to fftN/dsr
           (64 ms) earlier, so indexing by the start reports every beat early.
           Measured against single clicks at known times: the envelope peaked
           27-37 ms BEFORE the click (mean 31.9), and fftN/2/dsr is 32.0 —
           the correction is derived, not tuned. */
        beatOffset = frameTime(bestOff + shift, env);
        /* ⚠️ A confidence number for the phase was tried here and REMOVED. The
           comb's peakiness, (yc-mean)/(yc+mean), scored SPEECH at 32 and real
           music at a median of 5 (34-track corpus, 2026-09-09) — it ranks
           non-music above music, so as a gate it is worse than nothing. What
           did discriminate was self-consistency: the grid found from a window
           3 s later agreed within 60 ms on 28 of 33 tracks. If a gate is ever
           needed, measure THAT, do not resurrect this. */
    })();

    return {
        bpm: bpm,
        confidence: confidence,
        altBpm: second.bpm > 0 ? Math.round(second.bpm * 10) / 10 : null,
        altConfidence: altConfidence,
        // Seconds to the first beat; null when the envelope is too short to
        // place one. See step 9 — and read the note there before trusting it
        // across a long track: the GRID it implies is only as good as `bpm`.
        beatOffset: beatOffset,
    };
}

/* Onset PICKING — the moments the music actually moves.
 *
 * Why this exists next to detectBPM: a beat GRID drawn from tempo+phase is only
 * as good as the tempo, and on the pinned-tempo corpus (34 style samples,
 * tests/playwright/bpm-truth.json) the tempo lands on the wrong metrical level
 * or a few percent off often enough that a grid stretched across a whole song
 * drifts seconds by the end — 4 % at 124 BPM is 7.7 s over three minutes.
 * Onsets need no tempo, no phase and cannot drift: they are read off the signal
 * where they happen, so they also work on material with no steady pulse at all.
 *
 * Peak picking after Dixon (2006): a frame is an onset when it is the local
 * maximum of the envelope, stands above a LOCAL mean (a quiet intro and a loud
 * chorus differ by an order of magnitude in flux, so a fixed threshold can only
 * serve one of them), and is not too close to the previous one.
 *
 *   var list = window.__detectOnsets(audioBuffer);
 *   // [{ t: 1.243, strength: 0.82 }, ...]  t in seconds, strength 0..1
 */
function detectOnsets(ab, opts){
    var env = onsetEnvelope(ab);
    if (!env) return [];
    var flux = env.flux, n = flux.length;
    var o = opts || {};
    var framesPerSec = env.dsr / env.hop;
    // Two onsets closer than this are one event as far as a cut is concerned.
    var minGap = Math.max(1, Math.round((o.minGapSec != null ? o.minGapSec : 0.09) * framesPerSec));
    var localW = Math.max(3, Math.round(0.30 * framesPerSec));   // +/- 0.30 s
    var peakW  = Math.max(1, Math.round(0.05 * framesPerSec));   // +/- 0.05 s
    var mult   = o.threshold != null ? o.threshold : 1.6;        // times the local mean

    // Prefix sums so the local mean is O(1) per frame.
    var pre = new Float64Array(n + 1);
    for (var i = 0; i < n; i++) pre[i + 1] = pre[i] + flux[i];
    function localMean(i){
        var a = Math.max(0, i - localW), b = Math.min(n, i + localW + 1);
        return (pre[b] - pre[a]) / (b - a);
    }

    var out = [], lastF = -1e9, maxStrength = 0;
    for (var f = 1; f < n - 1; f++) {
        var v = flux[f];
        if (v <= 0) continue;
        var lm = localMean(f);
        if (!(v > lm * mult)) continue;
        var isPeak = true;
        for (var k = Math.max(0, f - peakW); k <= Math.min(n - 1, f + peakW); k++) {
            if (flux[k] > v) { isPeak = false; break; }
        }
        if (!isPeak) continue;
        if (f - lastF < minGap) {
            // Keep the stronger of two events inside one gap.
            if (out.length && v > out[out.length - 1]._v) {
                out[out.length - 1] = { t: frameTime(f, env), _v: v, strength: 0 };
                lastF = f;
            }
            continue;
        }
        out.push({ t: frameTime(f, env), _v: v, strength: 0 });
        lastF = f;
        if (v > maxStrength) maxStrength = v;
    }
    for (var j = 0; j < out.length; j++) {
        out[j].strength = maxStrength > 0 ? Math.min(1, out[j]._v / maxStrength) : 0;
        delete out[j]._v;
    }
    return out;
}

window.__detectBPM = detectBPM;
window.__detectOnsets = detectOnsets;
/* Сырая огибающая — для beat-grid.js, который строит СЕТКУ долей, а не одно
   число темпа. Считать её второй раз он не может и не должен: это тот самый
   «ещё один good enough» chroma, о котором предупреждает шапка key-detector.
   Возвращается как есть: { flux, dsr, hop, fftN }. */
window.__onsetEnvelope = onsetEnvelope;
})();
